/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ConversationWindow.tsx - Ventana flotante de conversación
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Ventana flotante completa con mensajes, input y funcionalidades
 * @version 1.0.1-pre.0.18
 * @architecture WASP Frontend
 * 
 * NUEVAS CARACTERÍSTICAS v1.0.1-pre.0.18:
 * - ✅ Sistema de countdown de inactividad (0.05 min → 0.1 min warning)
 * - ✅ Overlay translúcido con blur durante countdown
 * - ✅ Bloqueo de input cuando chat está en Modo IA
 * - ✅ Border verde visual en Modo Manual
 * - ✅ Reset automático de timer al detectar actividad
 * - ✅ Botones Cancelar/Volver a IA en overlay
 * - ✅ Auto-resume a Modo IA después de countdown
 * - ✅ Cleanup de timers al desmontar componente
 * - ✅ Integración con sistema de memoria
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Form, InputGroup, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faMinus,
  faPaperPlane,
  faSmile,
  faPaperclip,
  faEdit,
  faCheck,
  faExpand,
  faGripVertical,
  faClock,
  faRobot,
  faUser,
  faBan,
  faUnlock
} from '@fortawesome/free-solid-svg-icons';
import MessagesList from './MessagesList';
import EmojiPicker from './EmojiPicker';
import FileAttachment, { AttachedFile } from './FileAttachment';
import { useMessages } from '../hooks/useMessages';
import { useContactNames } from '../hooks/useContactNames';

interface ConversationWindowProps {
  id: string;
  conversationId: string;
  displayName: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
  onClose: () => void;
  onMinimize: () => void;
  onPositionChange: (position: { x: number; y: number }) => void;
  onSizeChange: (size: { width: number; height: number }) => void;
  onBringToFront: () => void;
  
  // ══════════════════════════════════════════════════════════════════════════
  // NUEVAS PROPS - Sistema de Memoria y Estados IA v1.0.1-pre.0.18
  // ══════════════════════════════════════════════════════════════════════════
  isPaused?: boolean;              // Estado pausa individual del usuario
  copilotEnabled?: boolean;        // Estado global IA del sistema (Switch 3)
  onTogglePause?: () => void;      // Callback para toggle pause/resume
  isTogglingPause?: boolean;       // Estado de loading durante toggle
  isBanned?: boolean;              // Estado de ban del usuario
  onToggleBan?: () => void;        // Callback para ban/unban
  isTogglingBan?: boolean;         // Loading durante operación ban/unban
}

/**
 * Componente ConversationWindow
 * 
 * Ventana flotante completa para gestionar una conversación
 * 
 * SISTEMA DE TIMERS v1.0.1-pre.0.18:
 * 
 * 1. Timer Principal (0.1) minutos):
 *    - Se activa solo cuando isPaused = true (Modo Manual)
 *    - Se resetea cada vez que el usuario escribe o envía mensaje
 *    - Al cumplirse, muestra el countdown overlay
 * 
 * 2. Timer Countdown (60 segundos):
 *    - Se activa después del timer principal
 *    - Muestra overlay translúcido con cuenta regresiva
 *    - Usuario puede cancelar o volver a IA inmediatamente
 *    - Al llegar a 0, auto-resume a Modo IA
 * 
 * BLOQUEO DE INPUT:
 * - Input deshabilitado cuando isPaused = false (Modo IA)
 * - Border verde cuando isPaused = true (Modo Manual)
 * - Tooltip explicativo según estado
 * 
 * @example
 * ```tsx
 * <ConversationWindow
 *   id="window-1"
 *   conversationId="56912345678"
 *   displayName="Jorge"
 *   position={{ x: 100, y: 100 }}
 *   size={{ width: 400, height: 600 }}
 *   zIndex={1000}
 *   isPaused={false}
 *   copilotEnabled={true}
 *   onClose={handleClose}
 *   onMinimize={handleMinimize}
 *   onTogglePause={handleTogglePause}
 * />
 * ```
 */
export const ConversationWindow: React.FC<ConversationWindowProps> = ({
  id,
  conversationId,
  displayName: initialDisplayName,
  position,
  size,
  zIndex,
  onClose,
  onMinimize,
  onPositionChange,
  onSizeChange,
  onBringToFront,
  isPaused = false,
  copilotEnabled = true,
  onTogglePause,
  isTogglingPause = false,
  isBanned = false,
  onToggleBan,
  isTogglingBan = false
}) => {
  // Hooks
  const {
    displayedMessages,
    loading,
    loadingMore,
    sending,
    error,
    hasMoreMessages,
    loadMoreMessages,
    sendMessage,
    refreshMessages
  } = useMessages({
    contactId: conversationId,
    autoUpdate: true,
    updateInterval: 10000
  });

  const { getContactName, setContactName } = useContactNames();

  // Estados locales existentes
  const [messageText, setMessageText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // ══════════════════════════════════════════════════════════════════════════
  // NUEVOS ESTADOS - Sistema de Timers v1.0.1-pre.0.18
  // ══════════════════════════════════════════════════════════════════════════
  const [showCountdownOverlay, setShowCountdownOverlay] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(60);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Refs existentes
  const windowRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Drag & Resize states
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Nombre display
  const displayName = getContactName(conversationId, initialDisplayName);

  // ══════════════════════════════════════════════════════════════════════════
  // SISTEMA DE TIMERS - v1.0.1-pre.0.18
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Limpiar todos los timers
   */
  const clearAllTimers = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  /**
   * Iniciar timer de inactividad (0.1 minutos)
   */
  const startInactivityTimer = () => {
    clearAllTimers();
    
    // Solo iniciar si está en Modo Manual (isPaused = true)
    if (!isPaused) return;

    inactivityTimerRef.current = setTimeout(() => {
      // Después de 0.30 min, mostrar countdown
      setShowCountdownOverlay(true);
      startCountdownTimer();
    }, 0.30 * 60 * 1000); // 0.30 minutos
  };

  /**
   * Iniciar countdown de 0.05 segundos
   */
  const startCountdownTimer = () => {
    // CORRECCIÓN: Usar 60 (segundos) como valor inicial
    setCountdownSeconds(10); 
    
    countdownTimerRef.current = setInterval(() => {
      setCountdownSeconds(prev => {
        // CORRECCIÓN: La condición de terminación es 0, no 1. 
        // Se debe ejecutar la acción cuando prev SÍ es 1, para que la siguiente acción
        // lo ponga en 0.
        if (prev <= 1) { 
          // Countdown terminado → Auto-resume a Modo IA
          handleAutoResumeAI();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  /**
   * Resetear timer de inactividad (al escribir o enviar)
   */
  const resetInactivityTimer = () => {
    if (isPaused && !showCountdownOverlay) {
      startInactivityTimer();
    }
  };

  /**
   * Cancelar countdown y reiniciar timer completo
   */
  const handleCancelCountdown = () => {
    clearAllTimers();
    setShowCountdownOverlay(false);
    setCountdownSeconds(60);
    startInactivityTimer(); // Reinicia 0.1 min completos
  };

  /**
   * Volver a Modo IA inmediatamente (desde botón)
   */
  const handleReturnToAI = () => {
    clearAllTimers();
    setShowCountdownOverlay(false);
    setCountdownSeconds(60);
    
    // Llamar al toggle para volver a IA
    if (onTogglePause) {
      onTogglePause();
    }
  };

  /**
   * Auto-resume cuando countdown llega a 0
   */
  const handleAutoResumeAI = () => {
    clearAllTimers();
    setShowCountdownOverlay(false);
    setCountdownSeconds(60);
    
    // Llamar al toggle automáticamente
    if (onTogglePause) {
      onTogglePause();
    }
  };

  /**
   * Formatear countdown (MM:SS)
   */
  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Effect: Iniciar/detener timer según isPaused
   */
  useEffect(() => {
    if (isPaused) {
      // Modo Manual activado: Iniciar timer de inactividad, a menos que ya
      // estemos mostrando el overlay (lo que indica que el countdown está corriendo).
      if (!showCountdownOverlay) {
         // Asegura que todas las funciones de limpieza internas están disponibles
        const startTimer = () => startInactivityTimer();
        startTimer();
      }
      
    } else {
      // Modo IA activado: Limpiar todo
      clearAllTimers();
      setShowCountdownOverlay(false);
      setCountdownSeconds(60);
    }

    // Cleanup: Solo debe ejecutarse al desmontar el componente.
    // También se ejecuta antes de cada re-ejecución del effect, por eso
    // es crucial que 'showCountdownOverlay' NO esté en las dependencias 
    // cuando el countdown está activo.
    return () => {
      clearAllTimers();
    };
  }, [isPaused]);

  // ══════════════════════════════════════════════════════════════════════════
  // FUNCIONES EXISTENTES (PRESERVADAS)
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Iniciar edición de nombre
   */
  const startEditingName = () => {
    setIsEditingName(true);
    setEditingName(displayName);
  };

  /**
   * Guardar nombre editado
   */
  const saveEditedName = async () => {
    if (editingName.trim() === displayName) {
      setIsEditingName(false);
      return;
    }

    setSavingName(true);
    const success = await setContactName(conversationId, editingName.trim());
    setSavingName(false);

    if (success) {
      setIsEditingName(false);
    }
  };

  /**
   * Cancelar edición de nombre
   */
  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditingName(displayName);
  };

  /**
   * Manejar envío de mensaje
   * MODIFICADO v1.0.1-pre.0.18: Reset timer al enviar
   */
  const handleSendMessage = async () => {
    if (!messageText.trim() && attachedFiles.length === 0) return;

    const success = await sendMessage(messageText.trim());

    if (success) {
      setMessageText('');
      setAttachedFiles([]);
      setShowEmojiPicker(false);
      
      // NUEVO: Reset timer de inactividad
      resetInactivityTimer();
    }
  };

  /**
   * Manejar selección de emoji
   */
  const handleEmojiSelect = (emoji: string) => {
    setMessageText(prev => prev + emoji);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  /**
   * Manejar selección de archivos
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: AttachedFile[] = [];

    Array.from(files).forEach(file => {
      const fileType = file.type.split('/')[0];
      let type: AttachedFile['type'] = 'other';

      if (fileType === 'image') type = 'image';
      else if (fileType === 'video') type = 'video';
      else if (fileType === 'audio') type = 'audio';
      else type = 'document';

      // Preview para imágenes
      if (type === 'image') {
        const reader = new FileReader();
        reader.onload = (e) => {
          newFiles.push({
            file,
            type,
            preview: e.target?.result as string
          });
          setAttachedFiles(prev => [...prev, ...newFiles]);
        };
        reader.readAsDataURL(file);
      } else {
        newFiles.push({ file, type });
      }
    });

    if (newFiles.length > 0 && !newFiles.some(f => f.type === 'image')) {
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Remover archivo adjunto
   */
  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Drag - Inicio
   */
  const handleDragStart = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    
    const target = e.target as HTMLElement;
    if (!target.closest('.window-header-draggable')) return;

    e.preventDefault();
    setIsDragging(true);
    onBringToFront();

    const rect = windowRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  /**
   * Resize - Inicio
   */
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    onBringToFront();

    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  };

  /**
   * Drag/Resize - Mover
   */
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;

        onPositionChange({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }

      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;

        const newWidth = Math.max(320, resizeStart.width + deltaX);
        const newHeight = Math.max(400, resizeStart.height + deltaY);

        onSizeChange({
          width: Math.min(newWidth, window.innerWidth - position.x),
          height: Math.min(newHeight, window.innerHeight - position.y)
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, resizeStart, position, size]);

  /**
   * Tecla Enter para enviar
   * MODIFICADO v1.0.1-pre.0.18: Reset timer al escribir
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // NUEVO: Reset timer al escribir (cualquier tecla)
    resetInactivityTimer();

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DETERMINAR ESTADO DE INPUT
  // ══════════════════════════════════════════════════════════════════════════
const isInputDisabled = !isPaused || sending || isTogglingPause || isBanned;
const inputTooltip = isBanned
  ? "Usuario bloqueado - No se pueden enviar mensajes"
  : !isPaused 
    ? "Chat en Modo IA - Pausa la IA para escribir manualmente" 
    : undefined;

  return (
    <div
  ref={windowRef}
  className={`conversation-window ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
  style={{
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
    zIndex: zIndex + 10000, 
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bs-body-bg)',
        borderRadius: '12px',
        border: 'none',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        overflow: 'hidden',
        transition: isDragging || isResizing ? 'none' : 'all 0.3s ease'
      }}
      onMouseDown={(e) => {  // ✅ Cambiar onClick por onMouseDown
    e.stopPropagation();
    onBringToFront();
  }}
    >
      {/* Header */}
      <div
        className="window-header window-header-draggable"
        style={{
          padding: '12px 16px',
          backgroundColor: 'var(--bs-body-bg)',
          color: 'var(--bs-body-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          borderBottom: '2px solid var(--bs-border-color)'
        }}
        onMouseDown={handleDragStart}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          <FontAwesomeIcon icon={faGripVertical} style={{ opacity: 0.5, color: 'var(--bs-secondary)' }} />
          
          {isEditingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <Form.Control
                type="text"
                size="sm"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEditedName();
                  if (e.key === 'Escape') cancelEditingName();
                }}
                autoFocus
                style={{
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  border: 'none',
                  fontSize: '0.95rem'
                }}
                disabled={savingName}
              />
              <Button
                variant="light"
                size="sm"
                onClick={saveEditedName}
                disabled={savingName}
                style={{ padding: '2px 8px' }}
              >
                {savingName ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FontAwesomeIcon icon={faCheck} />
                )}
              </Button>
            </div>
          ) : (
            <>
              <strong
                style={{
                  fontSize: '1rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {displayName}
              </strong>
              <Button
                variant="link"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  startEditingName();
                }}
                style={{
                  color: 'var(--bs-body-color)',
                  padding: '2px 6px',
                  opacity: 0.7
                }}
                title="Editar nombre"
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
  {/* ✅ NUEVO: BOTÓN BAN/UNBAN */}
  <div
    onClick={(e) => {
      e.stopPropagation();
      if (onToggleBan && !isTogglingBan) {
        onToggleBan();
      }
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      borderRadius: '20px',
      background: isBanned 
        ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
        : 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
      cursor: isTogglingBan ? 'wait' : 'pointer',
      transition: 'all 0.3s ease',
      border: '2px solid rgba(255,255,255,0.2)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      opacity: isTogglingBan ? 0.7 : 1
    }}
    title={isBanned ? "Usuario bloqueado - Click para desbloquear" : "Usuario desbloqueado - Click para bloquear"}
  >
    <FontAwesomeIcon 
      icon={isBanned ? faBan : faUnlock} 
      style={{ 
        fontSize: '0.85rem',
        color: '#fff',
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
      }} 
    />
    <span style={{
      fontSize: '0.7rem',
      fontWeight: 600,
      color: '#fff',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
      userSelect: 'none'
    }}>
      {isTogglingBan ? '...' : isBanned ? "Ban" : "OK"}
    </span>
  </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* SWITCH TOGGLE IA/MANUAL - Movido aquí fuera del área draggable */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (onTogglePause && !isTogglingPause) {
                onTogglePause();
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '20px',
              background: isPaused 
                ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              cursor: isTogglingPause ? 'wait' : 'pointer',
              transition: 'all 0.3s ease',
              border: '2px solid rgba(255,255,255,0.2)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              opacity: isTogglingPause ? 0.7 : 1
            }}
            title={isPaused ? "Modo Manual - Click para activar IA" : "Modo IA - Click para pausar"}
          >
            <FontAwesomeIcon 
              icon={isPaused ? faUser : faRobot} 
              style={{ 
                fontSize: '0.85rem',
                color: '#fff',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
              }} 
            />
            <span style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              userSelect: 'none'
            }}>
              {isTogglingPause ? '...' : isPaused ? "Manual" : "IA"}
            </span>
          </div>
          
          <Button
            variant="link"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onMinimize();
            }}
            style={{ color: 'var(--bs-secondary)', padding: '4px 8px', textDecoration: 'none' }}
            title="Minimizar"
          >
            <FontAwesomeIcon icon={faMinus} />
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{ color: 'var(--bs-danger)', padding: '4px 8px', textDecoration: 'none' }}
            title="Cerrar"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      </div>
      </div>

      {/* Mensajes */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <MessagesList
          messages={displayedMessages}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMoreMessages}
          error={error}
          onLoadMore={loadMoreMessages}
          autoScroll={true}
        />
      </div>

      {/* Archivos adjuntos */}
      {attachedFiles.length > 0 && (
        <FileAttachment
          files={attachedFiles}
          onRemove={handleRemoveFile}
          compact={true}
        />
      )}

      {/* Input de mensaje */}
      <div
        style={{
          padding: '12px',
          borderTop: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          position: 'relative'
        }}
      >
        {/* Emoji Picker */}
        {showEmojiPicker && (
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
            position="top"
          />
        )}

<div style={{
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-end'
}}>
  {/* Botones emoji + adjuntar - Circulares delicados */}
  <div style={{ display: 'flex', gap: '6px' }}>
    <button
      onClick={() => fileInputRef.current?.click()}
      disabled={isInputDisabled}
      title={isInputDisabled && !isPaused ? "Deshabilitado en Modo IA" : "Adjuntar archivo"}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: isInputDisabled ? '#e9ecef' : '#f0f0f0',
        color: isInputDisabled ? '#adb5bd' : '#6c757d',
        cursor: isInputDisabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1rem',
        transition: 'all 0.2s ease',
        opacity: isInputDisabled ? 0.5 : 1,
        boxShadow: 'none'
      }}
      onMouseEnter={(e) => {
        if (!isInputDisabled) {
          e.currentTarget.style.backgroundColor = '#e0e0e0';
          e.currentTarget.style.transform = 'scale(1.05)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#f0f0f0';
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      <FontAwesomeIcon icon={faPaperclip} />
    </button>

    <button
      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
      disabled={isInputDisabled}
      title={isInputDisabled && !isPaused ? "Deshabilitado en Modo IA" : "Emojis"}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: isInputDisabled ? '#e9ecef' : '#f0f0f0',
        color: isInputDisabled ? '#adb5bd' : '#6c757d',
        cursor: isInputDisabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1rem',
        transition: 'all 0.2s ease',
        opacity: isInputDisabled ? 0.5 : 1,
        boxShadow: 'none'
      }}
      onMouseEnter={(e) => {
        if (!isInputDisabled) {
          e.currentTarget.style.backgroundColor = '#e0e0e0';
          e.currentTarget.style.transform = 'scale(1.05)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#f0f0f0';
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      <FontAwesomeIcon icon={faSmile} />
    </button>
  </div>

  {/* TextArea - Estilo Apple refinado */}
  <textarea
    ref={textareaRef}
    rows={2}
    value={messageText}
    onChange={(e) => setMessageText(e.target.value)}
    onKeyDown={handleKeyDown}
    placeholder={isPaused ? "Mensaje..." : "Chat en Modo IA"}
    disabled={isInputDisabled}
    title={inputTooltip}
    style={{
      flex: 1,
      resize: 'none',
      width: '10%',
      fontSize: '0.95rem',
      padding: '10px 14px 5px',
      borderRadius: '18px',
      border: isPaused 
        ? '1.5px solid rgba(56, 239, 125, 0.5)' 
        : '1.5px solid #e0e0e0',
      backgroundColor: isPaused ? '#fff' : '#f8f9fa',
      color: isInputDisabled ? '#adb5bd' : '#212529',
      cursor: isInputDisabled ? 'not-allowed' : 'text',
      opacity: isInputDisabled ? 0.7 : 1,
      transition: 'all 0.25s ease',
      outline: 'none',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: '0.6',
      boxShadow: isPaused 
        ? '0 0 0 3px rgba(56, 239, 125, 0.08)' 
        : 'none'
    }}
    onFocus={(e) => {
      if (isPaused) {
        e.currentTarget.style.borderColor = 'rgba(56, 239, 125, 0.8)';
        e.currentTarget.style.boxShadow = '0 0 0 4px rgba(56, 239, 125, 0.12)';
      }
    }}
    onBlur={(e) => {
      if (isPaused) {
        e.currentTarget.style.borderColor = 'rgba(56, 239, 125, 0.5)';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56, 239, 125, 0.08)';
      }
    }}
  />

  {/* Botón enviar - Circular delicado estilo iOS */}
  <button
    onClick={handleSendMessage}
    disabled={isInputDisabled || (!messageText.trim() && attachedFiles.length === 0)}
    title={isInputDisabled && !isPaused ? "Deshabilitado en Modo IA" : "Enviar"}
    style={{
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      border: 'none',
      background: (isInputDisabled || (!messageText.trim() && attachedFiles.length === 0))
        ? '#e0e0e0'
        : 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
      color: '#fff',
      cursor: (isInputDisabled || (!messageText.trim() && attachedFiles.length === 0))
        ? 'not-allowed'
        : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.9rem',
      transition: 'all 0.2s ease',
      opacity: (isInputDisabled || (!messageText.trim() && attachedFiles.length === 0))
        ? 0.5
        : 1,
      boxShadow: (isInputDisabled || (!messageText.trim() && attachedFiles.length === 0))
        ? 'none'
        : '0 2px 8px rgba(0, 123, 255, 0.3)'
    }}
    onMouseEnter={(e) => {
      if (!isInputDisabled && (messageText.trim() || attachedFiles.length > 0)) {
        e.currentTarget.style.transform = 'scale(1.08)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.4)';
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 123, 255, 0.3)';
    }}
  >
    {sending ? (
      <Spinner animation="border" size="sm" style={{ width: '16px', height: '16px' }} />
    ) : (
      <FontAwesomeIcon icon={faPaperPlane} />
    )}
  </button>
</div>

        {/* Input file oculto */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* COUNTDOWN OVERLAY - NUEVO v1.0.1-pre.0.18 */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {showCountdownOverlay && isPaused && (
        <div 
          className="countdown-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'overlayFadeIn 0.3s ease-out'
          }}
        >
          <div 
            className="countdown-card"
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              textAlign: 'center',
              animation: 'cardSlideIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <FontAwesomeIcon 
                icon={faClock} 
                style={{ 
                  fontSize: '3.5rem', 
                  color: '#ffc107',
                  animation: 'clockPulse 2s infinite'
                }} 
              />
            </div>

            <h4 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 600, 
              marginBottom: '16px',
              color: '#333'
            }}>
              Modo Manual Expirando
            </h4>

            <div 
              className="countdown-display"
              style={{
                fontSize: '3rem',
                fontWeight: 700,
                fontFamily: 'monospace',
                color: countdownSeconds <= 10 ? '#dc3545' : '#007bff',
                margin: '24px 0',
                animation: countdownSeconds <= 10 ? 'numberPulse 1s infinite' : 'none'
              }}
            >
              {formatCountdown(countdownSeconds)}
            </div>

            <p style={{ 
              fontSize: '1rem', 
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              La conversación volverá a <strong>Modo IA</strong> automáticamente
            </p>

            <div 
              className="countdown-actions"
              style={{ 
                display: 'flex', 
                gap: '12px',
                justifyContent: 'center'
              }}
            >
              <Button
                variant="outline-secondary"
                onClick={handleCancelCountdown}
                style={{
                  padding: '10px 24px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: '8px',
                  minWidth: '140px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
                Cancelar
              </Button>
              
              <Button
                variant="primary"
                onClick={handleReturnToAI}
                style={{
                  padding: '10px 24px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: '8px',
                  minWidth: '140px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none'
                }}
              >
                <FontAwesomeIcon icon={faRobot} style={{ marginRight: '8px' }} />
                Volver a IA
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Resize handle */}
      <div
        className="resize-handle"
        onMouseDown={handleResizeStart}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '24px',
          height: '24px',
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 50%, #007bff 50%)',
          borderBottomRightRadius: '13px',
          opacity: 0.7,
          transition: 'opacity 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
      />

      <style>{`
        /* ═══════════════════════════════════════════════════════════════════ */
        /* ESTILOS EXISTENTES - PRESERVADOS */
        /* ═══════════════════════════════════════════════════════════════════ */
        
        .conversation-window {
          animation: windowSlideIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        @keyframes windowSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .conversation-window:hover {
          box-shadow: 0 6px 24px rgba(0,0,0,0.2);
        }

        .conversation-window.dragging,
        .conversation-window.resizing {
          box-shadow: 0 10px 40px rgba(0,0,0,0.3) !important;
          user-select: none;
        }

        /* ═══════════════════════════════════════════════════════════════════ */
        /* NUEVOS ESTILOS - COUNTDOWN OVERLAY v1.0.1-pre.0.18 */
        /* ═══════════════════════════════════════════════════════════════════ */

        @keyframes overlayFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes cardSlideIn {
          from {
            opacity: 0;
            transform: scale(0.8) translateY(-30px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes clockPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        @keyframes numberPulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        /* ═══════════════════════════════════════════════════════════════════ */
        /* RESPONSIVE - PRESERVADO + MEJORADO */
        /* ═══════════════════════════════════════════════════════════════════ */

        @media (max-width: 768px) {
          .conversation-window {
            width: 100vw !important;
            height: 100vh !important;
            top: 0 !important;
            left: 0 !important;
            border-radius: 0 !important;
          }

          .resize-handle {
            display: none;
          }

          .countdown-card {
            padding: 24px !important;
            width: 85% !important;
          }

          .countdown-display {
            font-size: 2.5rem !important;
          }

          .countdown-actions {
            flex-direction: column !important;
          }

          .countdown-actions button {
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ConversationWindow;